import { useState } from 'react';
import { Button, ButtonSelector, Modal, Text, TextArea } from '@belong/ui';
import { STRINGS } from 'maintenance/strings';
import type { MaintenanceQuoteRejectionReason } from 'maintenance/types/quote-centric';

const REJECTION_REASONS: {
  label: string;
  value: MaintenanceQuoteRejectionReason;
}[] = [
  { label: STRINGS['quote-questions-pricing'], value: 'TooExpensive' },
  {
    label: STRINGS['quote-questions-work'],
    value: 'WantSecondOpinionFromAnotherPro',
  },
  { label: STRINGS['quote-questions-other'], value: 'Other' },
];

const MIN_NOTES_LENGTH = 10;

type QuoteQuestionsModalProps = {
  isOpen: boolean;
  isSubmitting: boolean;
  onDismiss: () => void;
  onSubmit: (
    rejectionReason: MaintenanceQuoteRejectionReason,
    notes?: string,
  ) => void;
};

export const QuoteQuestionsModal = ({
  isOpen,
  isSubmitting,
  onDismiss,
  onSubmit,
}: QuoteQuestionsModalProps) => {
  const [rejectionReason, setRejectionReason] =
    useState<MaintenanceQuoteRejectionReason | null>(null);
  const [notes, setNotes] = useState('');

  const trimmedNotes = notes.trim();
  const isOther = rejectionReason === 'Other';
  const canSubmit =
    !!rejectionReason &&
    (!isOther || trimmedNotes.length >= MIN_NOTES_LENGTH) &&
    !isSubmitting;

  const handleSubmit = () => {
    if (!rejectionReason) return;
    onSubmit(rejectionReason, isOther ? trimmedNotes : undefined);
  };

  return (
    <Modal
      isOpen={isOpen}
      name="quote_questions"
      onDismiss={onDismiss}
      preventDismissOnOutsideClick
      size="auto"
      overlayClassName="z-fullscreen"
      contentClassName="sm:w-default-modal md:w-default-modal"
    >
      <div className="flex flex-col items-center gap-xl px-sm py-2xl sm:px-xl">
        <div className="flex flex-col gap-xs text-center">
          <Text variant="h2" fontWeight="semibold">
            {STRINGS['quote-questions-title']}
          </Text>
          <Text>{STRINGS['quote-questions-subtitle']}</Text>
        </div>

        <div className="flex w-full flex-col gap-xl">
          <ButtonSelector<MaintenanceQuoteRejectionReason>
            options={REJECTION_REASONS.map(({ label, value }) => ({
              value,
              renderContent: (isSelected) => (
                <Text
                  fontWeight="semibold"
                  className={isSelected ? 'text-white' : 'text-navy'}
                >
                  {label}
                </Text>
              ),
            }))}
            value={rejectionReason as MaintenanceQuoteRejectionReason}
            onChange={setRejectionReason}
            itemHeight="fluid"
            containerClassName="flex-col md:flex-col gap-sm"
            itemClassName="justify-start p-sm text-left"
          />

          {isOther && (
            <TextArea
              label={STRINGS['quote-questions-comments']}
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={200}
            />
          )}

          <div className="rounded border border-solid border-red bg-red-translucent-light p-sm">
            <Text>{STRINGS['quote-questions-warning']}</Text>
          </div>
        </div>

        <Button
          size="fluid"
          className="sm:w-[300px]"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {STRINGS['quote-questions-submit']}
        </Button>
      </div>
    </Modal>
  );
};
